"use client";

import {
  useEffect,
  useState,
  type ComponentType,
} from "react";
import { Button } from "@/components/ui/Button";
import { Container } from "@/components/ui/Container";
import { SplitHeading } from "@/components/ui/SplitHeading";
import { HeroCanvasFallback } from "@/components/three/HeroCanvasFallback";
import {
  isLiteGraphics,
  markLiteGraphics,
} from "@/components/three/animation-utils";
import type { HeroCanvasProps } from "@/components/three/HeroCanvas";
import type { HeroContent } from "@/lib/content/types";

type HeroSectionProps = {
  hero: HeroContent;
};

type HeroVisualMode = "pending" | "lite" | "full";

function markHeroCopySettled() {
  document.documentElement.setAttribute("data-hero-copy", "1");
}

/**
 * Hero copy is always in the DOM (visible from first paint / LCP-friendly).
 * Full WebGL: copy starts slightly lower and transforms up when intro settles.
 * Lite: static SVG + copy already settled.
 */
export function HeroSection({ hero }: HeroSectionProps) {
  const [mode, setMode] = useState<HeroVisualMode>("pending");
  const [HeroCanvas, setHeroCanvas] = useState<ComponentType<HeroCanvasProps> | null>(
    null,
  );

  useEffect(() => {
    if (isLiteGraphics()) {
      markLiteGraphics();
      setMode("lite");
      return;
    }

    document.documentElement.setAttribute("data-hero", "full");

    let cancelled = false;

    const load = () => {
      if (cancelled || isLiteGraphics()) {
        markLiteGraphics();
        setMode("lite");
        return;
      }
      void import("@/components/three/HeroCanvas").then((mod) => {
        if (cancelled || isLiteGraphics()) {
          markLiteGraphics();
          setMode("lite");
          return;
        }
        setHeroCanvas(() => mod.HeroCanvas);
        setMode("full");
      });
    };

    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(load, { timeout: 2200 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(id);
      };
    }

    const t = window.setTimeout(load, 500);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, []);

  return (
    <section className="relative flex min-h-dvh flex-col overflow-hidden bg-background">
      <div className="absolute inset-x-0 top-0 z-0 h-dvh w-full" id="hero-canvas">
        {mode === "full" && HeroCanvas ? (
          <HeroCanvas
            className="h-full w-full"
            onIntroComplete={markHeroCopySettled}
          />
        ) : mode === "lite" ? (
          <HeroCanvasFallback className="h-full w-full" />
        ) : (
          <div className="h-full w-full bg-background" aria-hidden />
        )}
      </div>

      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-[5] h-[48%] bg-gradient-to-b from-transparent via-black/70 to-black md:h-[42%]"
        aria-hidden="true"
      />

      <div className="hero-copy pointer-events-none relative z-10 flex min-h-dvh flex-col justify-end pb-10 pt-[48vh] sm:pb-12 sm:pt-[56vh] md:justify-start md:pb-16 md:pt-[66vh]">
        <Container className="pointer-events-auto">
          {hero.eyebrow ? (
            <p className="mb-4 max-w-xl text-xs uppercase tracking-[0.2em] text-muted sm:mb-5 sm:text-sm">
              {hero.eyebrow}
            </p>
          ) : null}

          <div className="max-w-4xl">
            <SplitHeading
              as="h1"
              className="text-[1.85rem] font-medium tracking-tight sm:text-4xl md:text-6xl lg:text-7xl lg:leading-[1.1]"
            >
              {hero.headline}
            </SplitHeading>
          </div>

          {hero.subheadline ? (
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted sm:mt-6 sm:text-lg md:text-xl">
              {hero.subheadline}
            </p>
          ) : null}

          <div className="pointer-events-auto mt-7 flex flex-col gap-3 sm:mt-10 sm:flex-row sm:gap-4">
            <Button href={hero.primaryHref}>{hero.primaryCta}</Button>
            <Button href={hero.secondaryHref} variant="secondary">
              {hero.secondaryCta}
            </Button>
          </div>
        </Container>
      </div>
    </section>
  );
}
